import type React from 'react';
import { Package, ChevronRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ImportRecord } from '@/types/models';

interface DeliveryListProps {
  deliveries: ImportRecord[];
  onSelect: (delivery: ImportRecord) => void;
  selectedId?: number | null;
  className?: string;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидает',
  processing: 'Обработка',
  partially_received: 'Частично принята',
  received: 'Принята',
  completed: 'Завершена',
};

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-blue-100 text-blue-700',
  partially_received: 'bg-orange-100 text-orange-700',
  received: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-green-100 text-green-700',
};

const DeliveryList: React.FC<DeliveryListProps> = ({ deliveries, onSelect, selectedId, className }) => {
  if (deliveries.length === 0) {
    return (
      <div className={cn('text-center py-12 text-gray-400', className)}>
        <Package className="w-10 h-10 mx-auto mb-2" />
        <p className="text-sm">Нет поставок</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {deliveries.map((delivery) => (
        <button
          key={delivery.import_id}
          onClick={() => onSelect(delivery)}
          className={cn(
            'w-full flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 text-left',
            selectedId === delivery.import_id
              ? 'border-primary-300 bg-primary-50 ring-1 ring-primary-200'
              : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50',
          )}
        >
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-gray-500" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {delivery.group_id}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(delivery.created_at)}
              </span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500">
                {delivery.orders_count} {delivery.orders_count === 1 ? 'заказ' : 'заказов'}
              </span>
            </div>
          </div>

          <span className={cn(
            'px-2 py-0.5 rounded-full text-xs font-medium shrink-0',
            statusColors[delivery.status] ?? 'bg-gray-100 text-gray-600',
          )}>
            {statusLabels[delivery.status] ?? delivery.status}
          </span>

          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      ))}
    </div>
  );
};

export default DeliveryList;
